import { FormEvent, useEffect, useState } from "react";
import {
  createEmailAccount,
  deleteEmailAccount,
  listEmailAccounts,
  EmailAccount,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

export default function EmailAccountsTab() {
  const toast = useToast();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // formulář pro přidání
  const [mailbox, setMailbox] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [adding, setAdding] = useState(false);

  function reload() {
    setLoading(true);
    listEmailAccounts()
      .then(setAccounts)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }

  useEffect(reload, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setAdding(true);
    try {
      await createEmailAccount({ mailbox, displayName, isDefault, active: true });
      toast.show("success", `Schránka ${mailbox} přidána.`);
      setMailbox("");
      setDisplayName("");
      reload();
    } catch (err: any) {
      toast.show("error", err.message ?? "Přidání selhalo.");
    } finally {
      setAdding(false);
    }
  }

  async function onDelete(a: EmailAccount) {
    if (!confirm(`Smazat schránku ${a.mailbox}?`)) return;
    try {
      await deleteEmailAccount(a.id);
      toast.show("success", "Schránka smazána.");
      reload();
    } catch (err: any) {
      toast.show("error", err.message ?? "Smazání selhalo.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Odesílací schránky (Microsoft Exchange)</h2>
        <p className="text-sm text-slate-600">
          E-maily (potvrzení rezervací, faktury) se posílají přes Microsoft Graph z vybrané schránky. Schránka
          musí existovat v Azure AD a aplikace musí mít oprávnění <code className="rounded bg-slate-100 px-1">Mail.Send</code>.
          Jedna je vždy označena jako výchozí.
        </p>

        {/* Seznam */}
        <div className="mt-5">
          {loading ? (
            <div className="text-slate-500">Načítám…</div>
          ) : accounts.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">
              Zatím žádná schránka. Přidej prvnú níže, jinak se nebudou odesílat e-maily.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
              {accounts.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{a.displayName}</span>
                      {a.isDefault && (
                        <span className="rounded bg-obloha-indigo-100 px-2 py-0.5 text-xs font-medium text-obloha-indigo-800">
                          Výchozí
                        </span>
                      )}
                      {!a.active && (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Neaktivní
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">{a.mailbox}</div>
                  </div>
                  <button onClick={() => onDelete(a)} className="text-sm text-red-600 hover:text-red-800">
                    Smazat
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Přidání */}
      <form onSubmit={onAdd} className="card space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Přidat schránku</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              E-mailová adresa <span className="text-red-500">*</span>
            </span>
            <input
              type="email"
              className="input"
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              placeholder="rezervace@kouzelnaobloha.cz"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Zobrazované jméno <span className="text-red-500">*</span>
            </span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Kouzelná Obloha — Rezervace"
              required
            />
          </label>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="h-4 w-4"
          />
          Nastavit jako výchozí (z této schránky se bude posílat vše)
        </label>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={adding}>
            {adding ? "Přidávám…" : "Přidat schránku"}
          </button>
        </div>
      </form>
    </div>
  );
}
